import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/services/firebase';
import type { SiteSettings } from '@/types';
import { siteSettings as defaults } from '@/data/site-content';

/**
 * PUBLIC (storefront) site settings, read live from Firestore `siteSettings/global`.
 *
 * WHY THIS EXISTS (DRB-21, 2026-08-11):
 *   The admin Settings page has always written to `siteSettings/global`, but NOTHING on the
 *   storefront read it back — `cart-helpers.ts` and `top-bar.tsx` imported the hardcoded
 *   build-time constant from `@/data/site-content` instead. So every field on that page
 *   (free-shipping threshold, flat rate, Subscribe & Save discount, announcement bar) was a
 *   STUB: the client could change it, the form would save, and the site would not move.
 *   Dr. Burns asked directly on 2026-08-10 — "Free shipping and Subscribe and save, can I edit
 *   those parameters from back end?" — and the honest answer was no. This makes it yes.
 *
 * The static seed is the pre-hydration fallback ONLY. Keep it in sync with the Firestore doc so
 * there is no visible flash between first paint and the live value.
 */
export function useSiteSettings(): { settings: SiteSettings; loading: boolean } {
  const [settings, setSettings] = useState<SiteSettings>(defaults);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onSnapshot(
      doc(db, 'siteSettings', 'global'),
      snap => {
        // Merge over defaults so a partially-populated doc can never blank out a value the
        // cart maths depends on (a missing freeShippingThreshold would make everything ship free).
        if (snap.exists()) setSettings({ ...defaults, ...(snap.data() as Partial<SiteSettings>) });
        setLoading(false);
      },
      () => setLoading(false) // read failure → keep defaults, never break the cart
    );
  }, []);

  return { settings, loading };
}
